import { useState, useCallback, useRef } from "react"
import { DistilleryService, AgentDetailedStatusResponse, agentExists } from "../distillery"

export interface EnsureAgentResult {
  status: AgentDetailedStatusResponse | null
  agentMissing: boolean // True if agent doesn't exist and user lacks permission to create it
  needsSetup: boolean // True if agent doesn't exist and user IS admin (needs to select user type)
}

interface UseDistilleryAgentReturn {
  // State
  agentStatus: AgentDetailedStatusResponse | null
  isInitializing: boolean
  initializationError: string | null

  // Actions
  ensureAgentConnected: (hasAdminPermission: boolean) => Promise<EnsureAgentResult>
  createAndConnectAgent: (userType: string) => Promise<AgentDetailedStatusResponse>
  verifyConnection: () => Promise<AgentDetailedStatusResponse | null>
  disconnectAgent: () => Promise<void>
  toggleAgent: () => Promise<void>
}

export const useDistilleryAgent = (): UseDistilleryAgentReturn => {
  const [agentStatus, setAgentStatus] = useState<AgentDetailedStatusResponse | null>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const [initializationError, setInitializationError] = useState<string | null>(null)
  const hasInitialized = useRef(false)

  /**
   * Checks if an agent exists and is connected for the organization.
   * This is called automatically when the ZEEF widget initializes.
   * Does NOT auto-create agents - returns needsSetup for admins to trigger user type selection.
   *
   * @param hasAdminPermission - Whether the current user has user-admin permission to create agents
   * @returns Object containing the agent status and whether setup is needed
   */
  const ensureAgentConnected = useCallback(
    async (hasAdminPermission: boolean): Promise<EnsureAgentResult> => {
      // Prevent duplicate initialization
      if (hasInitialized.current) {
        return { status: agentStatus, agentMissing: false, needsSetup: false }
      }

      setIsInitializing(true)
      setInitializationError(null)

      try {
        // Step 1: Check if agent exists
        const existingStatus = await DistilleryService.getAgentStatus()

        if (!agentExists(existingStatus)) {
          // Agent doesn't exist
          if (!hasAdminPermission) {
            // User lacks permission to create agent
            hasInitialized.current = true
            setIsInitializing(false)
            return { status: null, agentMissing: true, needsSetup: false }
          }

          // User is admin - needs to select user type before creating
          setIsInitializing(false)
          return { status: null, agentMissing: false, needsSetup: true }
        }

        // Agent exists - connect if needed
        if (existingStatus.connection_status !== "connected") {
          await DistilleryService.connectAgent()
        }

        // Get detailed status
        const detailedStatus = await DistilleryService.getDetailedStatus()
        setAgentStatus(detailedStatus)
        hasInitialized.current = true
        return { status: detailedStatus, agentMissing: false, needsSetup: false }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to initialize agent"
        console.error("Distillery agent initialization failed:", errorMessage)
        setInitializationError(errorMessage)
        hasInitialized.current = true // Mark as initialized even on error to prevent retries
        return { status: null, agentMissing: false, needsSetup: false }
      } finally {
        setIsInitializing(false)
      }
    },
    [agentStatus]
  )

  /**
   * Creates an agent with the specified user type and connects it.
   * Called after admin selects a user type from the setup modal.
   *
   * @param userType - The user type ID to assign to the agent
   * @returns The detailed agent status after creation and connection
   */
  const createAndConnectAgent = useCallback(async (userType: string): Promise<AgentDetailedStatusResponse> => {
    setIsInitializing(true)
    setInitializationError(null)

    try {
      await DistilleryService.createAgent(userType)
      await DistilleryService.connectAgent()
      const detailedStatus = await DistilleryService.getDetailedStatus()
      setAgentStatus(detailedStatus)
      hasInitialized.current = true
      return detailedStatus
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to create agent"
      console.error("Distillery agent creation failed:", errorMessage)
      setInitializationError(errorMessage)
      throw error
    } finally {
      setIsInitializing(false)
    }
  }, [])

  /**
   * Verifies the agent connection and reconnects if necessary.
   */
  const verifyConnection = useCallback(async (): Promise<AgentDetailedStatusResponse | null> => {
    try {
      const status = await DistilleryService.verifyAgentConnection()
      setAgentStatus(status)
      return status
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to verify agent connection"
      console.error("Distillery agent verification failed:", errorMessage)
      setInitializationError(errorMessage)
      return null
    }
  }, [])

  /**
   * Disconnects the agent from ZEEF.
   */
  const disconnectAgent = useCallback(async (): Promise<void> => {
    try {
      await DistilleryService.disconnectAgent()
      const status = await DistilleryService.getDetailedStatus()
      setAgentStatus(status)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to disconnect agent"
      console.error("Distillery agent disconnect failed:", errorMessage)
      throw error
    }
  }, [])

  /**
   * Toggles the agent's active state.
   */
  const toggleAgent = useCallback(async (): Promise<void> => {
    try {
      await DistilleryService.toggleAgent()
      const status = await DistilleryService.getDetailedStatus()
      setAgentStatus(status)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to toggle agent"
      console.error("Distillery agent toggle failed:", errorMessage)
      throw error
    }
  }, [])

  return {
    // State
    agentStatus,
    isInitializing,
    initializationError,

    // Actions
    ensureAgentConnected,
    createAndConnectAgent,
    verifyConnection,
    disconnectAgent,
    toggleAgent,
  }
}
